import { UserIcon } from "lucide-react";

import { LoadingIndicator, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { MyCandidaciesQueryParams } from "@/types";

const ALL_USERS_LABEL = "All users";
export const ALL_USERS_KEY = "all" as const;
export type PendingSelection = number | typeof ALL_USERS_KEY;

// Fully controlled. The page owns open/pending state so it can sequence the
// table render → label flip → close transition explicitly.
export const MyAviaryUserSelector = ({
  currentUserId,
  displayedQueryParams,
  open,
  pendingSelection,
  onOpenChange,
  onSelectAll,
  onSelectUser,
}: {
  currentUserId: number;
  // Snapshot of queryParams used to render the trigger label. The page keeps
  // this pinned to the previous selection until the new data has rendered.
  displayedQueryParams: MyCandidaciesQueryParams;
  open: boolean;
  pendingSelection: PendingSelection | null;
  onOpenChange: (open: boolean) => void;
  onSelectAll: () => void;
  onSelectUser: (userId: number) => void;
}) => {
  const { users, userNameMap } = useTaggableUsers();

  const selectedLabel = (() => {
    if (displayedQueryParams.allUsers) return ALL_USERS_LABEL;
    const effectiveId = displayedQueryParams.userId ?? currentUserId;
    return userNameMap[effectiveId] ?? "Unknown user";
  })();

  const renderPendingIndicator = (key: PendingSelection) =>
    pendingSelection === key ? (
      <LoadingIndicator className="ml-2" variant="xsmall" />
    ) : null;

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild className="w-48">
        <Button className="h-8" variant="outlineSecondary">
          <UserIcon className="h-4 w-4" /> {selectedLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[var(--radix-dropdown-menu-trigger-width)] p-0"
      >
        <DropdownMenuLabel>Viewing aviary for</DropdownMenuLabel>
        <DropdownMenuSeparator className="mb-0" />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onSelectAll();
          }}
        >
          <Row className="w-full items-center justify-between">
            <span>{ALL_USERS_LABEL}</span>
            {renderPendingIndicator(ALL_USERS_KEY)}
          </Row>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-0" />
        {users.map((user) => (
          <DropdownMenuItem
            key={user.id}
            onSelect={(e) => {
              e.preventDefault();
              onSelectUser(user.id);
            }}
          >
            <Row className="w-full items-center justify-between">
              <span>
                {user.firstName} {user.lastName}
              </span>
              {renderPendingIndicator(user.id)}
            </Row>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
